import { formatDate } from '@angular/common';
import { DATE_FORMATS } from '@app/core/models/date-adapter';
import { GanttColumn } from './gantt.types';

const numericTypes = new Set(['decimal', 'numeric', 'number', 'integer', 'int', 'bigint', 'smallint', 'tinyint',
    'unsigned integer', 'unsigned int', 'unsigned bigint', 'unsigned smallint', 'double', 'real', 'float', 'currency']);
const numberFormats = new Map<number | undefined, Intl.NumberFormat>();
const textSort = new Intl.Collator('nb-NO', { numeric: true, sensitivity: 'base' });
type ColumnValueType = 'number' | 'date' | 'datetime' | 'time' | 'text';

function valueType(column: GanttColumn, value: unknown): ColumnValueType {
    const type = column.coltype?.trim().toLowerCase().replace(/\(.*/, '');
    if (type) {
        if (numericTypes.has(type)) return 'number';
        if (type === 'timestamp' || type === 'datetime') return 'datetime';
        if (type === 'date' || type === 'time') return type;
        return 'text';
    }
    if (typeof value === 'number' || typeof value === 'bigint') return 'number';
    if (value instanceof Date) return 'datetime';
    if (typeof value === 'string' && columnDate(value)) return value.length === 10 ? 'date' : 'datetime';
    // Numeric-looking strings may be identifiers. Only typed numeric strings are converted.
    return 'text';
}

function columnDate(value: unknown): Date | undefined {
    if (value instanceof Date) return Number.isFinite(value.getTime()) ? value : undefined;
    if (typeof value !== 'string') return undefined;
    const match = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,7}))?)?(Z|[+-]\d{2}:?\d{2})?)?$/.exec(value);
    if (!match) return undefined;
    const [, year, month, day, hour = '0', minute = '0', second = '0', fraction = '', zone] = match;
    const milliseconds = +(fraction + '000').slice(0, 3);
    const civil = new Date(Date.UTC(+year, +month - 1, +day, +hour, +minute, +second, milliseconds));
    if (civil.getUTCFullYear() !== +year || civil.getUTCMonth() !== +month - 1 || civil.getUTCDate() !== +day
        || civil.getUTCHours() !== +hour || civil.getUTCMinutes() !== +minute || civil.getUTCSeconds() !== +second) return undefined;
    if (!zone) return new Date(+year, +month - 1, +day, +hour, +minute, +second, milliseconds); // SQL calendar wall time.
    const zoned = new Date(value.replace(' ', 'T'));
    return Number.isFinite(zoned.getTime()) ? zoned : undefined;
}

function columnNumber(value: unknown): number | bigint | undefined {
    if (typeof value === 'bigint') return value;
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
    if (typeof value !== 'string' || !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(value.trim())) return undefined;
    if (/^[+-]?\d+$/.test(value.trim())) return BigInt(value.trim());
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
}

function isEmpty(value: unknown): boolean { return value == null || value === '' || value === 'null'; }

/** Grid-compatible display formatting. Row values and save/navigation identities remain untouched. */
export function formatGanttColumnValue(value: unknown, column: GanttColumn): string {
    if (isEmpty(value)) return '';
    const type = valueType(column, value);
    if (type === 'number') {
        const number = columnNumber(value);
        if (number !== undefined) {
            const decimals = typeof column.decimals === 'number' && Number.isInteger(column.decimals)
                && column.decimals >= 0 && column.decimals <= 20 ? column.decimals : undefined;
            let formatter = numberFormats.get(decimals);
            if (!formatter) {
                formatter = new Intl.NumberFormat('nb-NO', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
                numberFormats.set(decimals, formatter);
            }
            return formatter.format(number);
        }
    } else if (type === 'time' && typeof value === 'string' && /^\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/.test(value)) {
        return value.slice(0, 5);
    } else if (type === 'date' || type === 'datetime' || type === 'time') {
        const date = columnDate(value);
        if (date) {
            const pattern = column.dateFormat || (type === 'time' ? 'HH:mm'
                : DATE_FORMATS.display.dateInput.format + (type === 'datetime' ? ', HH:mm' : ''));
            return formatDate(date, pattern, DATE_FORMATS.display.dateInput.language || 'nb-NO');
        }
    }
    return String(value);
}

/** Compare original values, keeping empty cells last in either direction. */
export function compareGanttColumnValues(left: unknown, right: unknown, column: GanttColumn, descending = false): number {
    if (isEmpty(left) || isEmpty(right)) return isEmpty(left) === isEmpty(right) ? 0 : isEmpty(left) ? 1 : -1;
    const leftType = valueType(column, left);
    const rightType = valueType(column, right);
    let comparison: number | undefined;
    if (leftType === 'number' && rightType === 'number') {
        const a = columnNumber(left), b = columnNumber(right);
        if (a !== undefined && b !== undefined) comparison = a < b ? -1 : a > b ? 1 : 0;
    } else if ((leftType === 'date' || leftType === 'datetime') && (rightType === 'date' || rightType === 'datetime')) {
        const a = columnDate(left), b = columnDate(right);
        if (a && b) comparison = Math.sign(a.getTime() - b.getTime());
    }
    comparison ??= Math.sign(textSort.compare(String(left), String(right)));
    return descending ? -comparison : comparison;
}

export function isGanttColumnSortable(column: GanttColumn): boolean {
    return column.sort !== false && !(typeof column.sort === 'string' && ['', '0', 'false', 'off'].includes(column.sort.toLowerCase()));
}

export function getGanttSortProperty(column: GanttColumn): string {
    return typeof column.sort === 'string' && !['1', 'true', 'on'].includes(column.sort.toLowerCase())
        ? column.sort : column.propertyKey ?? column.name ?? '';
}

/** Native Gantt templates accept HTML, while column values are plain text. */
export function escapeGanttColumnText(value: string): string {
    return value.replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]!));
}
